import { Link } from "react-router-dom";
import {
  BookOpen,
  XCircle,
  CircleCheck,
  Percent,
  UserX,
  Ban,
  RotateCcw,
  UserMinus,
  UserX as UserXAlt,
  Plane,
  ShieldAlert,
  CloudLightning,
  Search,
  Clock,
  Receipt,
  Scale,
  BookMarked,
  RefreshCw,
  Mail,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import useSeo from "../hooks/useSeo";

const SECTIONS = [
  {
    id: "definiciones",
    n: "1",
    icon: BookOpen,
    title: "Definiciones",
    body: (
      <>
        <h4>1.1 Huésped / Guest</h4>
        <p>
          Persona física autorizada y verificada que renta un vehículo mediante la plataforma
          RivoNes.
        </p>

        <h4>1.2 Anfitrión / Host</h4>
        <p>
          Propietario o poseedor legal autorizado de un vehículo publicado en RivoNes para renta
          temporal.
        </p>

        <h4>1.3 Viaje / Reservación</h4>
        <p>Periodo contratado por el huésped para el uso temporal del vehículo.</p>

        <h4>1.4 No-Show</h4>
        <p>
          Situación en la que alguna de las partes no se presenta al inicio del viaje dentro del
          tiempo permitido conforme a esta política.
        </p>

        <h4>1.5 Hora Oficial del Viaje</h4>
        <p>
          La hora registrada dentro de la plataforma RivoNes y visible en la reservación
          confirmada.
        </p>

        <h4>1.6 Fuerza Mayor</h4>
        <p>
          Eventos fuera del control razonable de cualquiera de las partes, incluyendo desastres
          naturales, cierres gubernamentales, disturbios civiles, accidentes graves, emergencias
          médicas, bloqueos fronterizos, actos delictivos, restricciones sanitarias, fallas masivas
          tecnológicas o situaciones similares.
        </p>
      </>
    ),
  },
  {
    id: "cancelacion-huesped",
    n: "2",
    icon: XCircle,
    title: "Cancelación por parte del Huésped",
    body: (
      <>
        <h4>2.1 Procedimiento de Cancelación</h4>
        <p>Si un huésped desea cancelar una reservación, deberá:</p>
        <ul>
          <li>Notificar al anfitrión inmediatamente mediante el sistema de mensajería oficial de RivoNes;</li>
          <li>Procesar la cancelación directamente desde la aplicación móvil o sitio web oficial de RivoNes.</li>
        </ul>
        <p>La cancelación tendrá efecto inmediato una vez confirmada en la plataforma.</p>
        <p>Los reembolsos dependerán de:</p>
        <ul>
          <li>El momento de cancelación;</li>
          <li>La duración de la reservación;</li>
          <li>El tipo de reservación;</li>
          <li>El tipo de tarifa seleccionada;</li>
          <li>Las circunstancias extraordinarias aplicables.</li>
        </ul>
        <p>
          Todos los tiempos se calcularán conforme a la zona horaria local del lugar de entrega del
          vehículo en México.
        </p>
      </>
    ),
  },
  {
    id: "reembolso-total",
    n: "3",
    icon: CircleCheck,
    title: "Reembolso Total",
    body: (
      <>
        <h4>3.1 Periodo de Cancelación Gratuita</h4>
        <p>El huésped recibirá un reembolso completo cuando:</p>

        <h4>A. Cancelación dentro del periodo gratuito</h4>
        <p>
          <strong>Reservaciones realizadas con más de 24 horas de anticipación:</strong> el huésped
          podrá cancelar gratuitamente hasta <strong>24 horas</strong> antes del inicio del viaje.
        </p>
        <p>
          <strong>Reservaciones realizadas dentro de las 24 horas previas al viaje:</strong> el
          huésped contará con <strong>1 hora</strong> después de realizar la reservación para
          cancelar gratuitamente.
        </p>

        <h4>B. Problemas de seguridad o limpieza</h4>
        <p>
          El huésped podrá cancelar sin penalización y recibir un reembolso completo si, al
          inspeccionar el vehículo:
        </p>
        <ul>
          <li>considera razonablemente que el vehículo no está limpio;</li>
          <li>considera que el vehículo es inseguro;</li>
          <li>detecta daños graves no reportados;</li>
          <li>detecta condiciones mecánicas peligrosas;</li>
          <li>observa documentos vehiculares inválidos o vencidos.</li>
        </ul>
        <p>Para ser elegible al reembolso total, el huésped deberá:</p>
        <ul>
          <li>NO aceptar el vehículo;</li>
          <li>NO iniciar el viaje;</li>
          <li>contactar inmediatamente al soporte oficial de RivoNes;</li>
          <li>proporcionar evidencia fotográfica o audiovisual.</li>
        </ul>

        <h4>C. Cancelación por mutuo acuerdo</h4>
        <p>
          Si anfitrión y huésped acuerdan cancelar la reservación y existe evidencia dentro del
          chat oficial de RivoNes, el huésped recibirá un reembolso completo.
        </p>

        <h4>D. Cancelaciones realizadas por RivoNes</h4>
        <p>RivoNes podrá cancelar cualquier viaje por motivos de:</p>
        <ul>
          <li>seguridad;</li>
          <li>fraude;</li>
          <li>verificación fallida;</li>
          <li>riesgo operativo;</li>
          <li>documentación inválida;</li>
          <li>actividad sospechosa;</li>
          <li>incumplimiento legal;</li>
          <li>investigación interna.</li>
        </ul>
        <p>En dichos casos, el huésped recibirá un reembolso completo.</p>
      </>
    ),
  },
  {
    id: "reembolso-parcial",
    n: "4",
    icon: Percent,
    title: "Reembolso Parcial",
    body: (
      <>
        <h4>4.1 Cancelaciones Fuera del Periodo Gratuito</h4>
        <p>Cuando el huésped cancele fuera del periodo gratuito aplicable:</p>

        <h4>Reservaciones mayores a 2 días</h4>
        <p>Se reembolsará:</p>
        <ul>
          <li>el total pagado MENOS el equivalente al promedio de 1 día de renta;</li>
          <li>cargos administrativos no reembolsables;</li>
          <li>tarifas de procesamiento de pago;</li>
          <li>cargos de protección aplicables.</li>
        </ul>

        <h4>Reservaciones de 2 días o menos</h4>
        <p>Se reembolsará:</p>
        <ul>
          <li>el total pagado MENOS el equivalente al 50% del promedio de 1 día de renta;</li>
          <li>tarifas administrativas aplicables.</li>
        </ul>

        <h4>4.2 Cargos Reembolsables</h4>
        <p>En cancelaciones elegibles, RivoNes podrá reembolsar:</p>
        <ul>
          <li>depósitos de seguridad;</li>
          <li>pagos por extras no utilizados;</li>
          <li>seguros opcionales no activados;</li>
          <li>tarifas de entrega según corresponda.</li>
        </ul>
        <p>
          Los tiempos bancarios de devolución pueden variar entre <strong>3 y 20 días hábiles</strong>{" "}
          dependiendo del banco o institución financiera.
        </p>
      </>
    ),
  },
  {
    id: "noshow-huesped",
    n: "5",
    icon: UserX,
    title: "No-Show del Huésped",
    body: (
      <>
        <h4>5.1 Definición de No-Show</h4>
        <p>Será considerado “No-Show” cuando:</p>
        <ul>
          <li>el huésped no se presenta dentro de los 60 minutos posteriores al inicio del viaje;</li>
          <li>el huésped no cancela previamente;</li>
          <li>el huésped no presenta licencia válida;</li>
          <li>el huésped presenta identificación falsa, vencida o alterada;</li>
          <li>el huésped envía a otra persona no autorizada;</li>
          <li>el huésped no completa los procesos de verificación requeridos;</li>
          <li>el huésped no responde durante el proceso de entrega.</li>
        </ul>

        <h4>5.2 Reembolsos por No-Show</h4>
        <p>
          <strong>Reservaciones mayores a 2 días:</strong> el huésped recibirá el total pagado
          MENOS el equivalente a <strong>2 días promedio</strong> de renta.
        </p>
        <p>
          <strong>Reservaciones de 2 días o menos:</strong> el huésped recibirá el total pagado
          MENOS el equivalente al <strong>75% de 1 día promedio</strong> de renta.
        </p>
      </>
    ),
  },
  {
    id: "no-reembolsables",
    n: "6",
    icon: Ban,
    title: "Viajes No Reembolsables",
    body: (
      <>
        <p>Algunas tarifas promocionales podrán identificarse como:</p>
        <ul>
          <li>“No Reembolsable”;</li>
          <li>“Precio Especial”;</li>
          <li>“Tarifa Promocional”.</li>
        </ul>
        <p>Estas reservaciones:</p>
        <ul>
          <li>NO generan reembolso fuera del periodo de gracia de 24 horas;</li>
          <li>
            NO permiten devolución parcial salvo disposición legal obligatoria o circunstancias
            extraordinarias aprobadas por RivoNes.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "devoluciones-anticipadas",
    n: "7",
    icon: RotateCcw,
    title: "Devoluciones Anticipadas",
    body: (
      <>
        <p>
          No se otorgarán créditos ni reembolsos por devoluciones anticipadas del vehículo, excepto
          cuando:
        </p>
        <ul>
          <li>el huésped solicite una modificación oficial del viaje;</li>
          <li>el anfitrión apruebe dicha modificación;</li>
          <li>la modificación quede registrada dentro de RivoNes.</li>
        </ul>
      </>
    ),
  },
  {
    id: "cancelacion-anfitrion",
    n: "8",
    icon: UserMinus,
    title: "Cancelación por parte del Anfitrión",
    body: (
      <>
        <h4>8.1 Obligación del Anfitrión</h4>
        <p>El anfitrión deberá:</p>
        <ul>
          <li>notificar inmediatamente al huésped;</li>
          <li>cancelar oficialmente mediante la plataforma.</li>
        </ul>

        <h4>8.2 Penalizaciones para el Anfitrión</h4>
        <p>Salvo excepciones aprobadas por RivoNes:</p>
        <div className="mt-3 grid sm:grid-cols-2 gap-3 not-prose">
          <div className="p-4 rounded-xl border border-red-200 bg-red-50">
            <p className="text-xs uppercase tracking-[0.18em] text-red-700 font-semibold">
              Con menos de 24 horas
            </p>
            <p className="font-heading font-bold text-2xl text-red-900 mt-1">
              $2,500 <span className="text-base font-semibold">MXN</span>
            </p>
            <p className="text-xs text-red-800/80 mt-1">Penalización de hasta</p>
          </div>
          <div className="p-4 rounded-xl border border-amber-200 bg-amber-50">
            <p className="text-xs uppercase tracking-[0.18em] text-amber-700 font-semibold">
              Con más de 24 horas
            </p>
            <p className="font-heading font-bold text-2xl text-amber-900 mt-1">
              $1,000 <span className="text-base font-semibold">MXN</span>
            </p>
            <p className="text-xs text-amber-800/80 mt-1">Penalización de hasta</p>
          </div>
        </div>
        <p>RivoNes podrá además:</p>
        <ul>
          <li>reducir visibilidad del anuncio;</li>
          <li>suspender temporalmente la cuenta;</li>
          <li>eliminar privilegios premium;</li>
          <li>limitar publicaciones;</li>
          <li>suspender permanentemente al anfitrión.</li>
        </ul>
      </>
    ),
  },
  {
    id: "noshow-anfitrion",
    n: "9",
    icon: UserXAlt,
    title: "No-Show del Anfitrión",
    body: (
      <>
        <p>Será considerado “No-Show del Anfitrión” cuando:</p>
        <ul>
          <li>no entregue el vehículo;</li>
          <li>no se presente dentro de los 30 minutos posteriores al inicio;</li>
          <li>entregue un vehículo distinto no aprobado;</li>
          <li>entregue documentación inválida;</li>
          <li>entregue un vehículo inseguro o ilegal.</li>
        </ul>
        <p>En estos casos:</p>
        <ul>
          <li>el huésped recibirá reembolso completo;</li>
          <li>
            RivoNes podrá aplicar penalizaciones de hasta <strong>$5,000 MXN</strong>;
          </li>
          <li>el anfitrión podrá ser suspendido.</li>
        </ul>
      </>
    ),
  },
  {
    id: "retrasos-transporte",
    n: "10",
    icon: Plane,
    title: "Retrasos de Vuelos, Autobuses o Trenes",
    body: (
      <>
        <p>Si el huésped sufre:</p>
        <ul>
          <li>retraso aéreo;</li>
          <li>cancelación de vuelo;</li>
          <li>pérdida de equipaje;</li>
          <li>retraso de autobús;</li>
          <li>cierre fronterizo;</li>
          <li>retraso migratorio extraordinario;</li>
        </ul>
        <p>deberá:</p>
        <ul>
          <li>notificar al anfitrión inmediatamente;</li>
          <li>solicitar modificación del horario;</li>
          <li>proporcionar evidencia válida.</li>
        </ul>
        <p>
          Si el anfitrión no puede acomodar el nuevo horario y el viaje debe cancelarse, RivoNes
          podrá emitir reembolso total o parcial según las circunstancias.
        </p>
      </>
    ),
  },
  {
    id: "seguridad-verificacion",
    n: "11",
    icon: ShieldAlert,
    title: "Cancelaciones por Seguridad y Verificación",
    body: (
      <>
        <p>RivoNes podrá cancelar cualquier reservación si:</p>
        <ul>
          <li>existen sospechas de fraude;</li>
          <li>la identidad no puede verificarse;</li>
          <li>existen inconsistencias documentales;</li>
          <li>existe riesgo de robo;</li>
          <li>existe actividad criminal sospechosa;</li>
          <li>se detecta uso de documentos falsos;</li>
          <li>se detectan pagos sospechosos.</li>
        </ul>
        <p>RivoNes podrá solicitar:</p>
        <ul>
          <li>INE;</li>
          <li>pasaporte;</li>
          <li>licencia mexicana o extranjera válida;</li>
          <li>selfie biométrica;</li>
          <li>comprobante de domicilio;</li>
          <li>documentos del vehículo;</li>
          <li>tarjeta de circulación;</li>
          <li>póliza de seguro.</li>
        </ul>
      </>
    ),
  },
  {
    id: "fuerza-mayor",
    n: "12",
    icon: CloudLightning,
    title: "Fuerza Mayor",
    body: (
      <>
        <p>RivoNes no será responsable por cancelaciones derivadas de:</p>
        <ul>
          <li>terremotos;</li>
          <li>inundaciones;</li>
          <li>huracanes;</li>
          <li>disturbios;</li>
          <li>actos gubernamentales;</li>
          <li>pandemias;</li>
          <li>bloqueos;</li>
          <li>conflictos sociales;</li>
          <li>fallas masivas tecnológicas;</li>
          <li>eventos fuera de control razonable.</li>
        </ul>
        <p>RivoNes evaluará caso por caso posibles créditos o reembolsos parciales.</p>
      </>
    ),
  },
  {
    id: "retencion-fondos",
    n: "13",
    icon: Search,
    title: "Investigaciones y Retención de Fondos",
    body: (
      <>
        <p>RivoNes podrá retener temporalmente pagos o depósitos cuando:</p>
        <ul>
          <li>exista investigación activa;</li>
          <li>exista disputa;</li>
          <li>exista daño pendiente;</li>
          <li>exista sospecha de fraude;</li>
          <li>existan multas o adeudos relacionados con el viaje.</li>
        </ul>
      </>
    ),
  },
  {
    id: "tiempos-reembolso",
    n: "14",
    icon: Clock,
    title: "Tiempos de Reembolso",
    body: (
      <>
        <p>Los reembolsos aprobados podrán reflejarse en:</p>
        <ul>
          <li>
            <strong>3 a 20 días hábiles</strong>;
          </li>
          <li>dependiendo del banco;</li>
          <li>procesador de pago;</li>
          <li>Mercado Pago;</li>
          <li>Stripe;</li>
          <li>SPEI;</li>
          <li>institución financiera utilizada.</li>
        </ul>
      </>
    ),
  },
  {
    id: "cargos-administrativos",
    n: "15",
    icon: Receipt,
    title: "Cargos Administrativos",
    body: (
      <>
        <p>RivoNes podrá retener cargos administrativos relacionados con:</p>
        <ul>
          <li>procesamiento de pagos;</li>
          <li>validación de identidad;</li>
          <li>prevención de fraude;</li>
          <li>soporte operativo;</li>
          <li>investigación de disputas;</li>
          <li>costos bancarios;</li>
          <li>recuperación de daños.</li>
        </ul>
      </>
    ),
  },
  {
    id: "disputas",
    n: "16",
    icon: Scale,
    title: "Disputas",
    body: (
      <>
        <p>Toda disputa deberá:</p>
        <ul>
          <li>
            presentarse dentro de las <strong>72 horas</strong> posteriores al viaje;
          </li>
          <li>incluir evidencia suficiente;</li>
          <li>realizarse mediante canales oficiales de RivoNes.</li>
        </ul>
        <p>
          RivoNes actuará como intermediario tecnológico y podrá tomar decisiones finales
          operativas para proteger la integridad de la plataforma.
        </p>
      </>
    ),
  },
  {
    id: "legislacion",
    n: "17",
    icon: BookMarked,
    title: "Legislación Aplicable",
    body: (
      <>
        <p>La presente política se rige conforme a:</p>
        <ul>
          <li>leyes federales de los Estados Unidos Mexicanos;</li>
          <li>Código de Comercio;</li>
          <li>Ley Federal de Protección al Consumidor;</li>
          <li>legislación aplicable de protección de datos;</li>
          <li>normativas fiscales y digitales mexicanas.</li>
        </ul>
        <p>
          Cualquier controversia será sometida a jurisdicción competente en México, salvo
          disposición legal obligatoria distinta.
        </p>
      </>
    ),
  },
  {
    id: "modificaciones",
    n: "18",
    icon: RefreshCw,
    title: "Modificaciones a esta Política",
    body: (
      <>
        <p>RivoNes podrá modificar esta política en cualquier momento para:</p>
        <ul>
          <li>cumplir leyes mexicanas;</li>
          <li>mejorar operaciones;</li>
          <li>proteger usuarios;</li>
          <li>actualizar procedimientos.</li>
        </ul>
        <p>La versión más reciente siempre estará disponible en:</p>
        <ul>
          <li>
            <Link to="/" className="text-terracotta font-semibold hover:underline">
              Rivones.com
            </Link>
          </li>
          <li>aplicación oficial RivoNes</li>
        </ul>
      </>
    ),
  },
  {
    id: "contacto",
    n: "19",
    icon: Mail,
    title: "Contacto Oficial",
    body: (
      <>
        <p>
          <strong>RivoNes — Atención al Cliente y Soporte Legal</strong>
        </p>
        <p>
          Correo electrónico:{" "}
          <a
            href="mailto:legal@rivones.com"
            className="text-terracotta font-semibold hover:underline"
          >
            legal@rivones.com
          </a>
        </p>
        <p>
          Sitio web:{" "}
          <a
            href="https://rivones.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-terracotta font-semibold hover:underline"
          >
            https://rivones.com
          </a>
        </p>
        <p>
          Operado por: <strong>Novus Core Technologies LLC</strong>
        </p>
      </>
    ),
  },
];

export default function CancelacionPage() {
  useSeo({
    title: "Política de Cancelación y Reembolsos · Rivones",
    description:
      "Política de Cancelación y Reembolsos de RivoNes México: cancelaciones por huésped y anfitrión, reembolsos totales y parciales, no-show, fuerza mayor y más.",
    keywords:
      "política de cancelación rivones, reembolsos rivones, cancelar renta de auto, no-show, fuerza mayor",
    canonical: "https://rivones.com/cancelacion",
  });

  return (
    <div className="bg-sand min-h-screen" data-testid="cancelacion-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-neutral-50 to-sand border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-[0.2em] text-terracotta uppercase">
            Documento legal
          </p>
          <h1
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#111111] tracking-tight mt-3"
            data-testid="cancelacion-h1"
          >
            Política de Cancelación y Reembolsos
          </h1>
          <p className="text-base sm:text-lg text-neutral-600 mt-4 leading-relaxed">
            RivoNes México — Operada por <strong>Novus Core Technologies LLC</strong>.
          </p>
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 mt-6 font-semibold">
            Última actualización · Mayo 2026
          </p>
        </div>
      </section>

      {/* Preamble */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16">
        <div className="space-y-4 text-neutral-700 leading-relaxed">
          <p>
            Bienvenido a <strong>RivoNes</strong>, la plataforma mexicana de renta de vehículos
            entre particulares operada por Novus Core Technologies LLC y sus afiliadas autorizadas
            en México.
          </p>
          <p>
            La presente Política de Cancelación y Reembolsos regula las cancelaciones,
            modificaciones, reembolsos, ausencias (“no-show”), retrasos y situaciones
            extraordinarias relacionadas con reservaciones realizadas a través de la plataforma
            RivoNes en los Estados Unidos Mexicanos.
          </p>
          <p>
            Al utilizar RivoNes, tanto anfitriones (“Hosts”) como huéspedes (“Guests” o “Usuarios”)
            aceptan expresamente esta política, así como los{" "}
            <Link to="/terminos" className="text-terracotta font-semibold hover:underline">
              Términos y Condiciones
            </Link>
            ,{" "}
            <Link to="/privacidad" className="text-terracotta font-semibold hover:underline">
              Aviso de Privacidad
            </Link>{" "}
            y demás políticas aplicables de la plataforma.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* TOC */}
        <nav
          aria-label="Índice"
          className="bg-white border border-neutral-200 rounded-2xl p-5 mb-10"
          data-testid="cancelacion-toc"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">
            Índice
          </p>
          <ol className="space-y-1.5 text-sm">
            {SECTIONS.map(({ id, n, title }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className="text-neutral-700 hover:text-terracotta transition-colors flex gap-2"
                >
                  <span className="text-neutral-400 tabular-nums">{n}.</span>
                  <span>{title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-12">
          {SECTIONS.map(({ id, n, icon: Icon, title, body }) => (
            <article
              key={id}
              id={id}
              data-testid={`cancelacion-section-${id}`}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-11 h-11 rounded-xl bg-neutral-100 items-center justify-center">
                  <Icon className="w-5 h-5 text-[#111111]" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 font-semibold">
                    Sección {n}
                  </p>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111111] leading-tight">
                    {title}
                  </h2>
                </div>
              </div>
              <div className="max-w-none text-neutral-700 leading-relaxed [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-3 [&_ul]:space-y-1 [&_li]:my-1 [&_strong]:text-[#111111] [&_strong]:font-semibold [&_h4]:font-semibold [&_h4]:text-base [&_h4]:mt-5 [&_h4]:mb-2 [&_h4]:text-[#111111]">
                {body}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 p-5 rounded-2xl bg-neutral-100 border border-neutral-200 text-sm text-neutral-600">
          Consulta también nuestros{" "}
          <Link to="/terminos" className="text-terracotta font-semibold hover:underline">
            Términos y Condiciones
          </Link>
          , la{" "}
          <Link to="/elegibilidad" className="text-terracotta font-semibold hover:underline">
            política de Elegibilidad y Verificación
          </Link>{" "}
          y el{" "}
          <Link to="/privacidad" className="text-terracotta font-semibold hover:underline">
            Aviso de Privacidad
          </Link>
          .
        </div>
      </section>
    </div>
  );
}
